import { useEffect, useRef, useState } from 'react';
import { setPageMeta } from '../utils/setPageMeta';

const EMAIL = 'chiechelski+leadingng@gmail.com';
const MAILTO = `mailto:${EMAIL}?subject=LeadingNG%20enquiry`;

// Custom hook for scroll animations
function useScrollAnimation(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return { ref, isVisible };
}

// Custom Cursor Component
function CustomCursor() {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      
      const target = e.target as HTMLElement;
      const isInteractive = target.closest('a, button');
      setIsHovering(!!isInteractive);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      <div 
        className={`custom-cursor ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
      <div 
        className={`custom-cursor-dot ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
    </>
  );
}

export default function Contact() {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const contactSection = useScrollAnimation(0.2);
  const mapSection = useScrollAnimation(0.3);

  useEffect(() => {
    setPageMeta({
      title: 'Contact LeadingNG | Project Enquiries & Quotes',
      description:
        'Contact LeadingNG for Web3, mobile, fintech and AI projects. Email, phone and Auckland office. Tell us about your product—we respond to serious technical enquiries.',
      canonicalPath: '/contact-us',
      keywords:
        'Contact LeadingNG, software project quote, Web3 development enquiry, hire Solana developer, Auckland software consultancy',
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = (window.scrollY / documentHeight) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <CustomCursor />
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} aria-hidden="true" />

      <section className="page-hero">
        <div className="hero-bg" style={{ transform: `translateY(${scrollY * 0.5}px)` }} />
        <div className="container text-center">
          <span className="page-hero-label animate-fade-in">Contact</span>
          <h1 className="page-title animate-slide-up" style={{ animationDelay: '0.1s' }}>Get in touch</h1>
          <p className="page-intro animate-fade-in" style={{ animationDelay: '0.3s' }}>
            Have a project in mind or want to know more? We'd love to hear from you.
          </p>
        </div>
      </section>

      <section ref={contactSection.ref} className={`section contact-section ${contactSection.isVisible ? 'visible' : ''}`}>
        <div className="container text-center">
          <span className="section-label scroll-reveal">Start your project</span>
          <h2 className="title scroll-reveal gradient-text" style={{ transitionDelay: '0.1s' }}>Phone, email, or visit</h2>
          <p className="intro scroll-reveal" style={{ transitionDelay: '0.2s' }}>
            Drop us a line or give us a ring—we're here to help.
          </p>
          <ul className="contact-info-inline scroll-reveal" style={{ transitionDelay: '0.3s' }}>
            <li><a href="tel:+64211220200" className="contact-link">+64 2112 20200</a></li>
            <li><a href={MAILTO} className="contact-link">Email us</a><span className="contact-email-display"> ({EMAIL})</span></li>
          </ul>
          <p className="contact-cta scroll-reveal" style={{ transitionDelay: '0.4s' }}>
            <a href={MAILTO} className="btn btn-cta btn-cta-primary magnetic-btn">
              <span className="btn-text">Open your email to contact us</span>
              <span className="btn-bg" />
            </a>
          </p>
          <p className="contact-note scroll-reveal" style={{ transitionDelay: '0.5s' }}>
            The button above opens your default email app with our address filled in.
          </p>
        </div>
      </section>

      <section ref={mapSection.ref} className={`map-section ${mapSection.isVisible ? 'visible' : ''}`}>
        <div className="map-placeholder">
          <div className="map-overlay scroll-reveal">
            <h4 className="map-title">LeadingNG</h4>
            <p className="map-address">
              145 Quay Street<br />
              Auckland CBD<br />
              NZ
            </p>
            <a
              href="https://www.google.com/maps/search/145+Quay+Street+Auckland+CBD"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-cta btn-cta-secondary magnetic-btn"
            >
              <span className="btn-text">Open in Google Maps</span>
              <span className="btn-bg" />
            </a>
          </div>
          <img
            src={`${process.env.PUBLIC_URL}/assets/images/staticmap.png`}
            alt="Map - 145 Quay Street, Auckland CBD"
            className="map-img"
            loading="lazy"
          />
        </div>
      </section>

      <style>{`
        /* Custom Cursor */
        .custom-cursor,
        .custom-cursor-dot {
          position: fixed;
          pointer-events: none;
          z-index: 10000;
          transform: translate(-50%, -50%);
        }
        .custom-cursor {
          width: 40px;
          height: 40px;
          border: 2px solid var(--color-primary);
          border-radius: 50%;
          transition: width 0.3s ease, height 0.3s ease, border-color 0.3s ease;
          opacity: 0.5;
        }
        .custom-cursor.hovering {
          width: 60px;
          height: 60px;
          border-color: var(--color-secondary);
          opacity: 0.8;
        }
        .custom-cursor-dot {
          width: 6px;
          height: 6px;
          background: var(--color-primary);
          border-radius: 50%;
          transition: transform 0.15s ease, background 0.3s ease;
        }
        .custom-cursor-dot.hovering {
          transform: translate(-50%, -50%) scale(1.5);
          background: var(--color-secondary);
        }

        /* Scroll Progress Bar */
        .scroll-progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          z-index: 9999;
          transition: width 0.1s ease-out;
          box-shadow: 0 0 10px var(--color-primary);
        }

        /* Hero animations */
        .animate-fade-in {
          opacity: 0;
          animation: fadeIn 1s ease-out forwards;
        }
        .animate-slide-up {
          opacity: 0;
          transform: translateY(30px);
          animation: slideUp 0.8s ease-out forwards;
        }
        @keyframes fadeIn {
          to { opacity: 1; }
        }
        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Scroll reveal animations */
        .scroll-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .visible .scroll-reveal {
          opacity: 1;
          transform: translateY(0);
        }

        /* Gradient animated text */
        .gradient-text {
          background: linear-gradient(90deg, 
            var(--color-primary) 0%, 
            var(--color-secondary) 50%, 
            var(--color-primary) 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 8s ease-in-out infinite;
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 100% center; }
        }

        /* Magnetic Button Effect */
        .magnetic-btn {
          position: relative;
          overflow: hidden;
          transform-style: preserve-3d;
          transition: transform 0.3s ease;
        }
        .magnetic-btn .btn-text {
          position: relative;
          z-index: 2;
        }
        .magnetic-btn .btn-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(255,255,255,0.2), transparent);
          transform: scale(0);
          transition: transform 0.5s ease;
          z-index: 1;
        }
        .magnetic-btn:hover .btn-bg {
          transform: scale(2.5);
        }
        .magnetic-btn::after {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, 
            transparent, 
            rgba(255,255,255,0.1), 
            transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }
        .magnetic-btn:hover::after {
          transform: translateX(100%);
        }
        .magnetic-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        }

        .page-hero {
          position: relative;
          padding: 88px 20px 64px;
          overflow: hidden;
          color: #fff;
        }
        .hero-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, var(--color-bg-dark) 0%, #1a252f 100%);
          z-index: 0;
        }
        .page-hero > .container {
          position: relative;
          z-index: 1;
        }
        .page-hero-label {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--color-primary);
          margin-bottom: 8px;
        }
        .page-title { margin: 0 0 12px; font-size: 2rem; font-weight: 700; letter-spacing: -0.02em; }
        .page-intro { margin: 0; opacity: 0.92; font-size: 1.05rem; }
        .contact-section { 
          background: linear-gradient(180deg, #f5f6f8 0%, #e8eaed 100%); 
          position: relative;
          overflow: hidden;
        }
        .contact-section::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(var(--color-primary-rgb), 0.03) 0%, transparent 70%);
          animation: rotateGradient 20s linear infinite;
        }
        @keyframes rotateGradient {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .contact-info-inline {
          list-style: none;
          margin: 0 0 28px;
          padding: 0;
          display: flex;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
        }
        .contact-link { 
          font-weight: 600; 
          font-size: 1.05rem;
          position: relative;
          transition: color 0.3s ease;
        }
        .contact-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--color-primary);
          transition: width 0.3s ease;
        }
        .contact-link:hover::after {
          width: 100%;
        }
        .contact-link:hover {
          color: var(--color-primary);
        }
        .contact-email-display { font-size: 0.95rem; opacity: 0.85; }
        .contact-cta { margin: 28px 0 12px; }
        .contact-note { margin: 0; font-size: 0.9rem; color: var(--color-text-muted); }
        .map-section { 
          padding: 48px 20px; 
          position: relative;
          overflow: hidden;
          background: #f5f6f8;
        }
        .map-placeholder { 
          position: relative;
          height: 400px;
          max-height: 50vh;
          max-width: 900px;
          margin: 0 auto;
          border-radius: var(--radius);
          overflow: hidden;
          box-shadow: var(--shadow-lg);
          transition: transform 0.3s ease;
        }
        .map-img { 
          width: 100%; 
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease, filter 0.5s ease;
        }
        .visible .map-img {
          animation: zoomIn 1s ease-out forwards;
        }
        @keyframes zoomIn {
          from {
            transform: scale(1.1);
            opacity: 0.8;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .map-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(10px);
          padding: 36px 42px;
          border-radius: var(--radius);
          text-align: center;
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--color-border);
          transition: box-shadow 0.3s ease, transform 0.3s ease;
          min-width: 320px;
          max-width: 90%;
        }
        .map-overlay:hover {
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          transform: translate(-50%, -52%);
        }
        .map-title { 
          margin: 0 0 12px; 
          font-size: 1.4rem; 
          font-weight: 700; 
          color: #1a1a1a;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .map-address { 
          margin: 0 0 24px; 
          font-size: 1.05rem; 
          color: var(--color-text-muted); 
          line-height: 1.8; 
        }

        /* Reduced motion preferences */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in,
          .animate-slide-up,
          .scroll-reveal {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
          .gradient-text,
          .contact-section::before {
            animation: none !important;
          }
          .custom-cursor,
          .custom-cursor-dot {
            display: none;
          }
          .scroll-progress-bar {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .custom-cursor,
          .custom-cursor-dot {
            display: none;
          }
          .scroll-progress-bar {
            height: 2px;
          }
          .map-section {
            padding: 32px 20px;
          }
          .map-placeholder {
            height: 350px;
          }
          .map-overlay {
            padding: 24px 28px;
            min-width: 280px;
          }
          .map-title {
            font-size: 1.2rem;
          }
          .map-address {
            font-size: 0.95rem;
            margin-bottom: 20px;
          }
        }

        @media (max-width: 480px) {
          .map-section {
            padding: 24px 16px;
          }
          .map-placeholder {
            height: 300px;
          }
          .map-overlay {
            padding: 20px 24px;
            min-width: auto;
            width: 90%;
          }
          .map-title {
            font-size: 1.1rem;
            margin-bottom: 10px;
          }
          .map-address {
            font-size: 0.9rem;
            margin-bottom: 16px;
          }
        }
      `}</style>
    </>
  );
}
